import { useMemo } from "react";
import { motion } from "framer-motion";
import { Phone, Monitor, Volume2, Check, UserPlus, Star } from "lucide-react";
import { CONTACT, HEROES, EVENT_PACKAGES } from "../data/gear";

const HERO_INITIAL = { opacity: 0, y: 24 };
const HERO_ANIMATE = { opacity: 1, y: 0 };
const HERO_TRANSITION = { duration: 0.6 };
const CARD_VIEWPORT = { once: true, margin: "-60px" };
const CARD_INITIAL = { opacity: 0, y: 24 };
const CARD_WHILE_IN_VIEW = { opacity: 1, y: 0 };

const groupIcon = (label) =>
  label === "Video" ? Monitor : Volume2;

const featuredStyles = {
  card: "bg-primary text-primary-foreground border-2 border-accent shadow-xl",
  title: "text-white",
  price: "text-accent",
  muted: "text-primary-foreground/70",
  blurb: "text-primary-foreground/85",
  includes: "text-primary-foreground/60",
  item: "text-primary-foreground/90",
  addonBox: "bg-white/10",
  btn: "bg-accent text-accent-foreground hover:bg-accent/90",
};

const plainStyles = {
  card: "bg-card text-foreground border border-border/60",
  title: "text-primary",
  price: "text-primary",
  muted: "text-muted-foreground",
  blurb: "text-muted-foreground",
  includes: "text-muted-foreground",
  item: "text-foreground",
  addonBox: "bg-secondary/60",
  btn: "bg-primary text-primary-foreground hover:bg-primary/90",
};

export default function CorporateEvents({ onQuote }) {
  return (
    <div data-testid="corporate-events-page">
      {/* HERO */}
      <section className="relative h-[62vh] min-h-[440px] flex items-end">
        <img src={HEROES.corporateEvents} alt="Corporate breakout room AV setup" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/25" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 w-full pb-14">
          <motion.div initial={HERO_INITIAL} animate={HERO_ANIMATE} transition={HERO_TRANSITION} className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.3em] text-accent mb-4">Corporate Events</p>
            <h1 className="font-heading text-5xl sm:text-6xl font-bold text-white tracking-tight">
              Breakout rooms, done right
            </h1>
            <p className="mt-4 text-lg text-white/85 max-w-xl leading-relaxed">
              Turn-key audio and video packages for meetings, conferences and breakout sessions across the Emerald Coast — professionally installed and operated.
            </p>
          </motion.div>
        </div>
      </section>

      {/* BANNER */}
      <div className="bg-accent text-accent-foreground">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-center">
          <p className="font-semibold tracking-wide">✓ Professional setup &amp; teardown included on every package</p>
          <a href={CONTACT.phoneHref} className="inline-flex items-center gap-2 text-sm underline underline-offset-4">
            <Phone className="w-4 h-4" /> {CONTACT.phone}
          </a>
        </div>
      </div>

      {/* PACKAGES */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="mb-12 max-w-2xl">
          <p className="text-xs uppercase tracking-[0.3em] text-accent mb-3">Event Packages</p>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-primary tracking-tight">
            Pick a package, we handle the rest
          </h2>
          <p className="mt-3 text-muted-foreground">
            Prices are per breakout room. Multi-day discounts apply automatically — pay the first-day rate, then the reduced additional-day rate.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 items-start">
          {EVENT_PACKAGES.map((pkg, i) => (
            <PackageCard key={pkg.name} pkg={pkg} index={i} onQuote={onQuote} />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="px-5 sm:px-8 pb-24">
        <div className="max-w-6xl mx-auto rounded-3xl bg-primary text-primary-foreground px-8 sm:px-16 py-14 text-center">
          <h2 className="font-heading text-4xl sm:text-5xl font-bold mb-4">Planning a multi-day conference?</h2>
          <p className="text-primary-foreground/80 max-w-xl mx-auto mb-8">
            Need several breakout rooms or a custom configuration? Tell us your dates and room count and we'll build a tailored quote.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              data-testid="events-cta-quote-btn"
              onClick={() => onQuote()}
              className="px-8 py-4 rounded-full bg-accent text-accent-foreground font-semibold uppercase tracking-widest text-sm hover:bg-accent/90 transition-colors"
            >
              Request a Quote
            </button>
            <a href={CONTACT.phoneHref} className="px-8 py-4 rounded-full border border-white/30 font-semibold uppercase tracking-widest text-sm hover:bg-white/10 transition-colors flex items-center justify-center gap-2">
              <Phone className="w-4 h-4" /> Call to Book
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

const PackageCard = ({ pkg, index, onQuote }) => {
  const slug = pkg.name.toLowerCase().replace(/\s+/g, "-");
  const s = pkg.featured ? featuredStyles : plainStyles;
  const transition = useMemo(() => ({ duration: 0.5, delay: index * 0.1 }), [index]);
  return (
    <motion.div
      data-testid={`package-card-${slug}`}
      initial={CARD_INITIAL}
      whileInView={CARD_WHILE_IN_VIEW}
      viewport={CARD_VIEWPORT}
      transition={transition}
      className={`relative rounded-2xl p-8 flex flex-col h-full ${s.card}`}
    >
      {pkg.featured && (
        <span className="absolute -top-3 left-8 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-accent text-accent-foreground text-[11px] font-semibold uppercase tracking-widest">
          <Star className="w-3 h-3" /> Most Popular
        </span>
      )}

      <h3 className={`font-heading text-3xl font-bold ${s.title}`}>
        {pkg.name}
      </h3>

      <div className="mt-4 flex items-baseline gap-2">
        <span className={`text-4xl font-bold ${s.price}`}>{pkg.priceFirst}</span>
        <span className={`text-sm ${s.muted}`}>first day</span>
      </div>
      <p className={`text-sm ${s.muted}`}>{pkg.priceAdd}</p>

      <p className={`mt-4 text-sm leading-relaxed ${s.blurb}`}>
        {pkg.blurb}
      </p>

      <div className="mt-6 space-y-6 flex-1">
        {pkg.groups.map((g) => {
          const Icon = groupIcon(g.label);
          return (
            <div key={g.label}>
              <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-[0.2em] font-semibold text-accent">
                <Icon className="w-4 h-4" /> {g.label}
              </div>
              <ul className="space-y-2">
                {g.items.map((it) => (
                  <li key={it} className="flex gap-2 text-sm leading-relaxed">
                    <Check className="w-4 h-4 mt-0.5 shrink-0 text-accent" />
                    <span className={s.item}>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}

        {pkg.includes && (
          <p className={`text-xs italic ${s.includes}`}>
            {pkg.includes}
          </p>
        )}

        {pkg.addon && (
          <div className={`flex gap-2 rounded-xl p-3 text-sm ${s.addonBox}`}>
            <UserPlus className="w-4 h-4 mt-0.5 shrink-0 text-accent" />
            <span className={s.item}>{pkg.addon}</span>
          </div>
        )}
      </div>

      <button
        data-testid={`package-quote-${slug}`}
        onClick={() => onQuote(pkg.name)}
        className={`mt-8 w-full px-6 py-3.5 rounded-full font-semibold uppercase tracking-widest text-sm transition-colors ${s.btn}`}
      >
        Request this Package
      </button>
    </motion.div>
  );
};
